import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useModal } from "./Modal";
import { useNavigate } from "react-router-dom";
import Icon from "./Icon";
import logoIces from "../assets/icesextendida2.png";

const Login = () => {
  const [loading, setLoading] = useState(false);
  const [mounted, setMounted] = useState(false);

  const {
    loginWithGoogle,
    currentUser,
    churchUser,
    loading: authLoading,
    authError,
    clearAuthError,
  } = useAuth();
  const { showAlert } = useModal();
  const navigate = useNavigate();

  useEffect(() => {
    // Animação de entrada
    const timer = setTimeout(() => setMounted(true), 50);
    return () => clearTimeout(timer);
  }, []);

  // Redirecionar se já estiver logado (aguarda churchUser para evitar loop)
  useEffect(() => {
    if (!authLoading && currentUser && churchUser) {
      navigate("/dashboard");
    }
  }, [currentUser, churchUser, authLoading, navigate]);

  const handleGoogleLogin = async () => {
    setLoading(true);
    if (authError) clearAuthError();
    try {
      await loginWithGoogle();
      // Navegação é feita pelo useEffect ao detectar currentUser + churchUser
    } catch (error) {
      console.error("Erro no login com Google:", error);
      showAlert({
        variant: "error",
        title: "Erro no login",
        message: "Erro no login com Google: " + error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        .login-page {
          min-height: 100vh;
          min-height: 100dvh;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1.5rem;
          position: relative;
          overflow: hidden;
          background: var(--color-background);
        }

        .login-page::before {
          content: '';
          position: absolute;
          top: -50%;
          left: -30%;
          width: 80%;
          height: 120%;
          background: radial-gradient(ellipse, var(--color-primary-light) 0%, transparent 70%);
          opacity: 0.6;
          pointer-events: none;
        }

        .login-page::after {
          content: '';
          position: absolute;
          bottom: -40%;
          right: -20%;
          width: 60%;
          height: 100%;
          background: radial-gradient(ellipse, var(--color-info-light) 0%, transparent 70%);
          opacity: 0.4;
          pointer-events: none;
        }

        .login-container {
          width: 100%;
          max-width: 400px;
          position: relative;
          z-index: 1;
          opacity: 0;
          transform: translateY(24px);
          transition: opacity 0.6s cubic-bezier(0.16, 1, 0.3, 1),
                      transform 0.6s cubic-bezier(0.16, 1, 0.3, 1);
        }

        .login-container.mounted {
          opacity: 1;
          transform: translateY(0);
        }

        .login-logo {
          display: block;
          max-width: 240px;
          width: 100%;
          height: auto;
          margin: 0 auto 2rem;
          filter: drop-shadow(0 4px 12px var(--color-shadow));
        }

        .login-card {
          background: var(--color-surface);
          border-radius: var(--radius-xl);
          border: 1px solid var(--color-border-light);
          box-shadow: var(--shadow-xl);
          overflow: hidden;
        }

        .login-card-inner {
          padding: 2.5rem 2rem 2rem;
        }

        .login-welcome {
          text-align: center;
          margin-bottom: 2rem;
        }

        .login-welcome h1 {
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--color-text);
          margin-bottom: 0.5rem;
          letter-spacing: -0.02em;
        }

        .login-welcome p {
          font-size: 0.95rem;
          color: var(--color-text-secondary);
          margin: 0;
        }

        .login-divider {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin: 1.75rem 0;
          color: var(--color-text-muted);
          font-size: 0.8rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .login-divider::before,
        .login-divider::after {
          content: '';
          flex: 1;
          height: 1px;
          background: var(--color-border);
        }

        .google-btn {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.75rem;
          padding: 0.875rem 1.5rem;
          border: 2px solid var(--color-border);
          border-radius: var(--radius-md);
          background: var(--color-background);
          color: var(--color-text);
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all var(--transition-base);
          position: relative;
          overflow: hidden;
        }

        .google-btn:hover:not(:disabled) {
          border-color: var(--color-primary);
          background: var(--color-primary-light);
          transform: translateY(-1px);
          box-shadow: var(--shadow-md);
        }

        .google-btn:active:not(:disabled) {
          transform: translateY(0);
          box-shadow: var(--shadow-sm);
        }

        .google-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .google-btn img {
          width: 22px;
          height: 22px;
          flex-shrink: 0;
        }

        .google-btn .spinner-border {
          width: 20px;
          height: 20px;
          border-width: 2px;
        }

        .login-features {
          display: flex;
          justify-content: center;
          gap: 1.5rem;
          margin-top: 2rem;
          padding-top: 1.5rem;
          border-top: 1px solid var(--color-border-light);
        }

        .login-feature {
          display: flex;
          align-items: center;
          gap: 0.4rem;
          color: var(--color-text-muted);
          font-size: 0.8rem;
        }

        .login-help {
          text-align: center;
          margin-top: 1.5rem;
        }

        .login-help a {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          color: var(--color-success);
          font-size: 0.9rem;
          font-weight: 500;
          text-decoration: none;
          padding: 0.625rem 1.25rem;
          border-radius: var(--radius-md);
          transition: all var(--transition-base);
          border: 1px solid transparent;
        }

        .login-help a:hover {
          background: var(--color-success-light);
          border-color: var(--color-success);
        }

        .login-back {
          text-align: center;
          margin-top: 0.75rem;
        }

        .login-back button {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          color: var(--color-text-muted);
          font-size: 0.9rem;
          font-weight: 500;
          background: none;
          border: 1px solid transparent;
          padding: 0.625rem 1.25rem;
          border-radius: var(--radius-md);
          cursor: pointer;
          transition: all var(--transition-base);
        }

        .login-back button:hover {
          color: var(--color-text);
          background: var(--color-background);
          border-color: var(--color-border);
        }

        @media (max-width: 480px) {
          .login-card-inner {
            padding: 2rem 1.5rem 1.5rem;
          }

          .login-logo {
            max-width: 200px;
            margin-bottom: 1.5rem;
          }

          .login-welcome h1 {
            font-size: 1.3rem;
          }

          .login-features {
            gap: 1rem;
          }
        }
      `}</style>

      <div className="login-page">
        <div className={`login-container ${mounted ? "mounted" : ""}`}>
          <img
            src={logoIces}
            alt="Igreja Cristã Evangélica Siqueira"
            className="login-logo"
          />

          <div className="login-card">
            <div className="login-card-inner">
              <div className="login-welcome">
                <h1>Bem-vindo de volta</h1>
                <p>Acesse o sistema da sua igreja</p>
              </div>

              {authError && (
                <div style={{
                  padding: '0.875rem 1rem',
                  marginBottom: '1rem',
                  borderRadius: 'var(--radius-md)',
                  backgroundColor: 'var(--color-danger-light, #fef2f2)',
                  border: '1px solid var(--color-danger, #ef4444)',
                  color: 'var(--color-danger, #ef4444)',
                  fontSize: '0.9rem',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '0.5rem',
                }}>
                  <Icon name="alert-circle" size={18} style={{ flexShrink: 0, marginTop: '1px' }} />
                  <span>{authError}</span>
                </div>
              )}

              <button
                type="button"
                className="google-btn"
                onClick={handleGoogleLogin}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span
                      className="spinner-border text-primary"
                      role="status"
                      aria-hidden="true"
                    />
                    Conectando...
                  </>
                ) : (
                  <>
                    <img src="/google.png" alt="" />
                    Entrar com Google
                  </>
                )}
              </button>

              <div className="login-features">
                <span className="login-feature">
                  <Icon name="shield" size={14} />
                  Seguro
                </span>
                <span className="login-feature">
                  <Icon name="zap" size={14} />
                  Rápido
                </span>
                <span className="login-feature">
                  <Icon name="phone" size={14} />
                  Mobile
                </span>
              </div>
            </div>
          </div>

          <div className="login-help">
            <a
              href="https://wa.me/qr/AUDCWZRJVOY3N1"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Icon name="message-circle" size={16} />
              Precisa de ajuda? Fale conosco
            </a>
          </div>

          <div className="login-back">
            <button type="button" onClick={() => navigate("/")}>
              <Icon name="arrow-left" size={16} />
              Voltar para Área de Visitantes
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;
